import React, { useState } from "react";

import { saveAs } from "file-saver";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { downloadDailySummaryApi } from "../../../services/netplusTeam";
import {
  useGetDailySummaryQuery,
  useGetRealEstateTeamOptionsQuery,
} from "../../../store/appApi";
import { TeamForm } from "../realestateteam/Teams";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const Kpi = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
      {value ?? 0}
    </div>
  </div>
);

const SectionTitle = ({ children, tone, action }) => (
  <div
    className={`flex flex-wrap items-center justify-between gap-2 rounded-2xl px-5 py-3 text-lg font-semibold text-white ${tone}`}
  >
    <span>{children}</span>
    {action}
  </div>
);

const MissingBanner = ({ names, date, today }) => {
  const when = date === today ? "today" : `on ${date}`;
  return names.length ? (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <b>No report submitted {when} by ({names.length}):</b> {names.join(", ")}
    </div>
  ) : (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
      Everyone has reported {when}.
    </div>
  );
};

// Only people who are absent (or on full-day leave) that day, by name.
const AbsentBanner = ({ people, date, today }) => {
  const when = date === today ? "today" : `on ${date}`;
  return people.length ? (
    <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
      <b>Absent {when} ({people.length}):</b>{" "}
      {people
        .map((p) =>
          p.tag === "ON_LEAVE" ? `${p.name} (on leave${p.leaveType ? `: ${p.leaveType}` : ""})` : p.name,
        )
        .join(", ")}
    </div>
  ) : (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
      No absentees {when}.
    </div>
  );
};

const AbsentTag = ({ absence }) =>
  absence ? (
    <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
      {absence.tag === "ON_LEAVE" ? "ON LEAVE" : "ABSENT"}
    </span>
  ) : null;

const Status = ({ ok }) =>
  ok ? (
    <span className="text-emerald-600">Submitted</span>
  ) : (
    <span className="font-semibold text-red-600">Not submitted</span>
  );

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-11-01" -> "01 Nov 2026"
const fmtDay = (k) => `${k.slice(8, 10)} ${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;

// A back-dated report: the day's entries were saved on a later day.
const LateTag = ({ days }) =>
  days?.length ? (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
      Submitted on {days.map(fmtDay).join(", ")}
    </span>
  ) : null;

const Badge = ({ tone, children }) => (
  <span className={`ml-2 rounded px-1.5 text-[10px] font-bold ${tone}`}>{children}</span>
);

// One person's day — the same block in every team they belong to: daily
// counts, construction work (with PM review), tasks and customer follow-ups.
const hasDayContent = (m, works = m.works) =>
  (m.items || []).length + (works || []).length + (m.tasks || []).length + (m.customerRemarks || []).length > 0;

const DayReport = ({ m, works = m.works }) => {
  const items = m.items || [];
  const tasks = m.tasks || [];
  const remarks = m.customerRemarks || [];
  works = works || [];
  if (!hasDayContent(m, works)) {
    return (
      <div className="mt-2 text-sm">
        {m.reported ? (
          <span className="text-gray-400">
            Sheet saved{m.forms?.length ? ` (${m.forms.join(", ")})` : ""}, no entries dated this day.
          </span>
        ) : (
          <span className="font-semibold text-red-600">Nothing reported for this day.</span>
        )}
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-2">
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, idx) => (
            <span key={idx} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
              {it.label}: <b>{it.value}</b>
            </span>
          ))}
        </div>
      )}
      {works.length > 0 && (
        <ul className="space-y-2">
          {works.map((w, i) => (
            <li key={i} className="rounded-xl bg-white/70 px-3 py-2 text-sm ring-1 ring-gray-100">
              <div className="font-medium text-gray-800">
                {[w.unit, w.mainWork, w.subWork].filter(Boolean).join(" › ") || "Work"}
                <span className="ml-2 text-xs font-normal text-gray-500">{w.status}</span>
                {w.started && <Badge tone="bg-blue-100 text-blue-700">STARTED</Badge>}
                {w.completedToday && <Badge tone="bg-emerald-100 text-emerald-700">COMPLETED</Badge>}
                {w.by && <span className="ml-2 text-xs font-normal text-gray-400">by {w.by}</span>}
              </div>
              {w.notes.map((n, j) => (
                <div key={j} className="text-gray-600">
                  • {n.text || "Work update"}
                  {n.peopleCount !== "" && n.peopleCount != null && (
                    <span className="text-gray-400"> ({n.peopleCount} people)</span>
                  )}
                </div>
              ))}
              {(w.review || []).length > 0 && (
                <div className="mt-1.5 rounded-lg bg-violet-50 px-2.5 py-1.5 text-xs text-violet-800">
                  <div className="font-semibold uppercase tracking-wide text-[10px] text-violet-500">
                    PM remarks / verification
                  </div>
                  {w.review.map((r, k) => (
                    <div key={k}>{r}</div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {tasks.length > 0 && (
        <ul className="space-y-2">
          {tasks.map((w, i) => (
            <li key={i} className="rounded-xl px-3 py-2 text-sm ring-1 ring-gray-100">
              <div className="font-medium text-gray-800">
                {[w.work, w.subWork].filter(Boolean).join(" › ")}
                <span className="ml-2 text-xs font-normal text-gray-500">{w.status}</span>
                {w.assigned && <Badge tone="bg-blue-100 text-blue-700">ASSIGNED TODAY</Badge>}
                {w.due && <Badge tone="bg-red-100 text-red-700">DUE TODAY</Badge>}
              </div>
              {w.notes.map((n, j) => (
                <div key={j} className="text-gray-600">
                  • {n.text}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
      {remarks.length > 0 && (
        <div className="rounded-xl px-3 py-2 text-sm ring-1 ring-gray-100">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Customer follow-ups ({remarks.length})
          </div>
          {remarks.map((c, i) => (
            <div key={i} className="text-gray-600">
              • <b className="font-medium text-gray-800">{c.customer}:</b> {c.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PersonHeader = ({ m, tag, extra }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <span className="font-medium text-gray-800">
      {m.name}
      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
        {m.role}
      </span>
      {tag}
      <AbsentTag absence={m.absence} />
    </span>
    <span className="flex items-center gap-3 text-xs text-gray-500">
      {extra}
      <Status ok={m.reported} />
      {m.reported && <LateTag days={m.submittedOn} />}
      {m.reportedViaTeam && <span className="text-gray-400">(via team)</span>}
    </span>
  </div>
);

// Netplus: only what was submitted in Staff Reporting (no Netplus Hub log).
const NetplusTeam = ({ t, date, today }) => {
  const m = t.managerReport;
  const mgr = t.members.find((x) => x.isManager);
  return (
    <div className={`${panel} space-y-4 p-5`}>
      <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {t.name} — Manager: {t.manager?.name || "—"}
      </div>
      <MissingBanner names={t.notSubmitted} date={date} today={today} />
      <AbsentBanner people={t.absentees} date={date} today={today} />
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-700">
          Manager daily report
          {m && <LateTag days={mgr?.submittedOn} />}
        </div>
        {m ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            <Kpi label="Pending cases" value={m.pendingCases} />
            <Kpi label="Visits" value={`${m.todayVisits} (Σ ${m.cumulative.todayVisits})`} />
            <Kpi label="Recovered" value={`${m.modemRecovered} (Σ ${m.cumulative.modemRecovered})`} />
            <Kpi label="Recharged" value={`${m.recharged} (Σ ${m.cumulative.recharged})`} />
            <Kpi label="Untraceable" value={`${m.untraceable} (Σ ${m.cumulative.untraceable})`} />
            <Kpi label="Balance" value={m.balance} />
          </div>
        ) : (
          <div className="text-sm text-gray-400">
            The manager has not added a daily report for this day.
          </div>
        )}
      </div>
      <div className="space-y-2">
        <div className="text-sm font-semibold text-gray-700">Staff reports</div>
        {t.members
          .filter((x) => !x.isManager)
          .map((x) => (
            <div key={x.employeeId} className="rounded-xl border border-gray-100 px-4 py-3 text-sm">
              <PersonHeader m={x} />
              <DayReport m={x} />
            </div>
          ))}
      </div>
    </div>
  );
};

const RealEstateTeam = ({ t, date, today }) => (
  <div className={`${panel} space-y-4 p-5`}>
    <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
      {t.name} — Team head: {t.manager?.name || "—"}
    </div>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi label="Open visits" value={t.totals.openVisits} />
      <Kpi label="Site visits" value={t.totals.siteVisits} />
      <Kpi label="Calls" value={t.totals.calls} />
      <Kpi label="Sales" value={t.totals.sales} />
    </div>
    <MissingBanner names={t.notSubmitted} date={date} today={today} />
    <AbsentBanner people={t.absentees} date={date} today={today} />
    <div className="space-y-2">
      {t.members.map((m) => (
        <div key={m.employeeId} className="rounded-xl border border-gray-100 px-4 py-3 text-sm">
          <PersonHeader
            m={m}
            tag={
              m.isManager && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                  TEAM HEAD
                </span>
              )
            }
          />
          <DayReport m={m} />
        </div>
      ))}
    </div>
  </div>
);

const EmployeeWork = ({ e, highlight }) => (
  <div
    className={`rounded-2xl border p-4 ${
      highlight ? "border-amber-300 bg-amber-50/40" : "border-gray-200 bg-white"
    }`}
  >
    <PersonHeader
      m={e}
      extra={
        <span>
          All work: <b>{e.overall.total}</b> · Done <b>{e.overall.completed}</b> · In progress{" "}
          <b>{e.overall.inProgress}</b> · On hold <b>{e.overall.onHold}</b>
        </span>
      }
    />
    {e.team && (
      <div className="mt-2 text-xs text-gray-500">
        Team head for ({e.team.length}):{" "}
        <span className="text-gray-700">
          {e.team.map((m) => `${m.name} (${m.role})`).join(", ") || "—"}
        </span>
      </div>
    )}
    <DayReport m={e} works={e.today} />
  </div>
);

const RevenueWork = ({ m }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4">
    <PersonHeader
      m={m}
      extra={
        <span>
          All tasks: <b>{m.overall.total}</b> · Done <b>{m.overall.completed}</b> · Pending{" "}
          <b>{m.overall.pending}</b>
        </span>
      }
    />
    <DayReport m={m} />
  </div>
);

const DailySummary = () => {
  const [date, setDate] = useState("");
  const { data, isFetching } = useGetDailySummaryQuery(date);
  const c = data?.construction;
  const [downloading, setDownloading] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const { data: teamOptions } = useGetRealEstateTeamOptionsQuery(undefined, {
    skip: !showTeamForm,
  });
  const r = data?.revenue;

  const download = async () => {
    try {
      setDownloading(true);
      const res = await downloadDailySummaryApi(date || data?.date);
      saveAs(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `daily-report-summary-${date || data?.date || ""}.xlsx`,
      );
    } catch (err) {
      Swal.fire({ icon: "error", title: "Download failed", text: "Please try again." });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Daily Report Summary" motherMenu="Reports" />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Daily Report Summary</h1>
              <p className="mt-1 text-sm text-gray-500">
                One day at a glance — Netplus, Real Estate Sales, Revenue and
                Construction. Only these teams report daily.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Date
                <input
                  type="date"
                  className={`${inputClass} mt-1 block`}
                  value={date || data?.date || ""}
                  max={data?.today}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
                disabled={!data || downloading}
                onClick={download}
              >
                {downloading ? "Preparing…" : "Download Excel"}
              </button>
            </div>
          </div>
        </section>

        {isFetching && !data && (
          <section className={`${panel} p-10 text-center text-gray-400`}>Loading…</section>
        )}

        {data && (
          <>
            {data.attendanceAvailable && (
              <AbsentBanner people={data.absentees} date={data.date} today={data.today} />
            )}
            <SectionTitle tone="bg-sky-700">Netplus</SectionTitle>
            {data.netplus.length === 0 && (
              <div className={`${panel} p-6 text-center text-gray-400`}>No Netplus team yet.</div>
            )}
            {data.netplus.map((t) => (
              <NetplusTeam key={t.teamId} t={t} date={data.date} today={data.today} />
            ))}

            <SectionTitle
              tone="bg-amber-700"
              action={
                data.realEstate.length > 0 && (
                  <button
                    type="button"
                    className="rounded-xl bg-white/15 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/25"
                    onClick={() => setShowTeamForm(true)}
                  >
                    + New team
                  </button>
                )
              }
            >
              Real Estate Sales
            </SectionTitle>
            {data.realEstate.length === 0 && (
              <div className={`${panel} space-y-3 p-8 text-center`}>
                <div className="text-base font-semibold text-gray-800">
                  No Real Estate team yet
                </div>
                <p className="mx-auto max-w-lg text-sm text-gray-500">
                  Pick the staff for the team and tick who the team head is. Their
                  daily reports will show up here from then on.
                </p>
                <button
                  type="button"
                  className="rounded-xl bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400"
                  onClick={() => setShowTeamForm(true)}
                >
                  Create Real Estate team
                </button>
              </div>
            )}
            {data.realEstate.map((t) => (
              <RealEstateTeam key={t.teamId} t={t} date={data.date} today={data.today} />
            ))}

            <SectionTitle tone="bg-violet-700">Revenue</SectionTitle>
            {r.members.length === 0 ? (
              <div className={`${panel} p-6 text-center text-gray-400`}>
                No staff with the Revenue role.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <Kpi label="Employees" value={r.totals.employees} />
                  <Kpi label="Reported" value={r.totals.reported} />
                  <Kpi label="Work items today" value={r.totals.workItemsToday} />
                </div>
                <MissingBanner names={r.notSubmitted} date={data.date} today={data.today} />
                <AbsentBanner people={r.absentees} date={data.date} today={data.today} />
                <div className="space-y-3">
                  {r.members.map((m) => (
                    <RevenueWork key={m.employeeId} m={m} />
                  ))}
                </div>
              </>
            )}

            <SectionTitle tone="bg-emerald-700">Construction</SectionTitle>
            {c.warnings?.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {c.warnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <Kpi label="Employees" value={c.totals.employees} />
              <Kpi label="Reported" value={c.totals.reported} />
              <Kpi label="Work items today" value={c.totals.workItemsToday} />
            </div>
            <MissingBanner names={c.notSubmitted} date={data.date} today={data.today} />
            <AbsentBanner people={c.absentees} date={data.date} today={data.today} />
            <div className="space-y-3">
              {c.teamHeads.length > 0 && (
                <>
                  <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Team heads
                  </div>
                  {c.teamHeads.map((e) => (
                    <div key={e.employeeId} className="space-y-2">
                      <EmployeeWork e={e} highlight />
                      {(e.teamMembers || []).length > 0 && (
                        <div className="space-y-2 border-l-4 border-amber-200 pl-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {e.name}'s team — each member's own report
                          </div>
                          {e.teamMembers.map((m) => (
                            <EmployeeWork key={m.employeeId} e={m} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
              <div className="pt-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Civil Engineers
              </div>
              {c.engineers.length === 0 && (
                <div className="text-sm text-gray-400">No civil engineers.</div>
              )}
              {c.engineers.map((e) => (
                <EmployeeWork key={e.employeeId} e={e} highlight />
              ))}
              <div className="pt-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Other site employees
              </div>
              {c.others.map((e) => (
                <EmployeeWork key={e.employeeId} e={e} />
              ))}
            </div>
          </>
        )}
      </div>
      <TeamForm
        show={showTeamForm}
        onHide={() => setShowTeamForm(false)}
        initial={null}
        options={teamOptions}
      />
    </>
  );
};

export default DailySummary;
